import { ActionTabs, Button, v2 } from '@fieldnation/platform-components';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Part,
  PartUsage,
  PartV2,
  PartsAddedBy,
  USAGE_CODES as USAGE_ENUMS,
} from './types.d';
import { convertPartDataToV1Payload, getWorkorderStatusId, USAGE_CODES } from './utils';
import styles from './styles.scss';
import { error, success } from '../legacy/flash-messenger';
import { createPartApi, updatePartApi, upsertWorkorderPartsAttribute } from './service';
import AddPart from './AddPart';
import Usage from './Usage';

const { Modal } = v2;

enum TABS {
  DETAILS = 'Details',
  USAGE = 'Usage',
}

const initialStateNewPart: Part = {
  id: 0,
  externalId: '',
  externalNumber: '',
  description: '',
  returnable: '',
  trackingNumber: '',
  carrier: '',
  carrierOther: '',
  returnInstruction: '',
};

const initialStatePartUsageData: PartUsage = {
  usageCode: '',
  customerRetained: '',
  returnTracking: '',
  returnCarrier: '',
  returnCarrierOtherName: '',
  note: '',
};

const PARTS_ADDED_BY_OPTIONS: { label: string; value: PartsAddedBy }[] = [
  { label: 'Buyer (Me)', value: 'buyer' },
  { label: 'Provider', value: 'assigned_provider' },
];

interface PartsFormProps {
  partData: Part;
  usageData: PartUsage;
  onStateChange?:
    | ((id: number, type: 'part' | 'usage', data: Part | PartUsage) => void)
    | null;
  onDelete?: ((id: number) => void) | null;
  fromWorkOrderCreate: boolean;
  openEditModal: boolean;
  setOpenEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  partsCorrelationId: string;
  workOrderId: number;
  workOrderProjectName: string;
  workOrderStatus: string;
  workOrderUserRole: string;
  usageCodes?: string[];
  addPart: boolean;
  isProvider: boolean;
  partsAddedBy?: PartsAddedBy | null;
  onPartsAddedByChange?: ((value: PartsAddedBy) => void) | null;
  hasExistingParts?: boolean;
}

const PartsForm = ({
  partData,
  usageData,
  onStateChange = null,
  onDelete = null,
  fromWorkOrderCreate = false,
  openEditModal = false,
  setOpenEditModal,
  partsCorrelationId,
  workOrderId = 0,
  workOrderProjectName = '',
  workOrderStatus = '',
  workOrderUserRole = '',
  usageCodes = [],
  addPart = false,
  isProvider = false,
  partsAddedBy = null,
  onPartsAddedByChange = null,
  hasExistingParts = false,
}: PartsFormProps): JSX.Element => {
  const [tab, setTab] = useState(TABS.DETAILS);
  const [newPart, setNewPart] = useState<Part>(partData ?? initialStateNewPart);
  const [partUsageData, setPartUsageData] = useState<PartUsage>(
    usageData ?? initialStatePartUsageData,
  );
  const [returnTrackingRequired, setReturnTrackingRequired] =
    useState<boolean>(false);
  const [isUniqueExternalId, setIsUniqueExternalId] = useState<boolean>(true);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [selectedPartsAddedBy, setSelectedPartsAddedBy] = useState<PartsAddedBy | null>(
    partsAddedBy ?? null,
  );

  useEffect(() => {
    // Not a provider and not all required fields are filled
    if (
      !isProvider &&
      (!newPart.externalId?.trim() ||
        !newPart.externalNumber?.trim() ||
        !newPart.description?.trim() ||
        !newPart.returnable?.trim() ||
        !newPart.trackingNumber?.trim())
    ) {
      setCanSubmit(false);
      return;
    }

    // Not a provider and externalId is not unique
    if (!isProvider && !isUniqueExternalId) {
      setCanSubmit(false);
      return;
    }

    // return tracking is required and returnable is yes but return tracking fields are not filled
    if (
      returnTrackingRequired &&
      newPart?.returnable === 'Yes' &&
      (!partUsageData.returnTracking?.trim() ||
        !partUsageData.returnCarrier?.trim() ||
        (partUsageData.returnCarrier === 'other' &&
          !partUsageData.returnCarrierOtherName?.trim()))
    ) {
      setCanSubmit(false);
      return;
    }

    if (
      partUsageData.usageCode === USAGE_ENUMS.USED &&
      !partUsageData.customerRetained &&
      newPart?.returnable === 'Yes'
    ) {
      setCanSubmit(false);
      return;
    }

    if (!newPart.returnInstruction) {
      setCanSubmit(false);
      return;
    }

    if (isProvider && !partUsageData?.usageCode) {
      setCanSubmit(false);
      return;
    }

    setCanSubmit(true);
  }, [newPart, partUsageData, returnTrackingRequired, isUniqueExternalId]);

  const onClose = () => {
    if (!fromWorkOrderCreate) {
      setOpenEditModal(false);
    }
  };

  const handlePartsAddedByChange = async (value: PartsAddedBy) => {
    setSelectedPartsAddedBy(value);
    if (onPartsAddedByChange) {
      onPartsAddedByChange(value);
    }
    if (workOrderId) {
      const companyId = window?.context?.company?.id || 0;
      await upsertWorkorderPartsAttribute(workOrderId, value, workOrderUserRole, companyId);
    }
  };

  const handleCreateAndUpdate = async () => {
    const companyId = window?.context?.company?.id || 0;
    const statusId = getWorkorderStatusId(workOrderStatus);

    const payload = convertPartDataToV1Payload(newPart, partUsageData);
    let partResponse: PartV2 | undefined;

    if (addPart) {
      partResponse = await createPartApi(
        workOrderId,
        workOrderUserRole,
        companyId,
        statusId,
        payload,
      );
    } else {
      partResponse = await updatePartApi(
        workOrderId,
        newPart.id,
        workOrderUserRole,
        companyId,
        statusId,
        payload,
      );
    }

    if (!partResponse) {
      error('Failed to save part information, please try again later.');
      return;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-unused-expressions
    window?._fnRedux?.dispatch({
      type: 'UPDATE_WORK_ORDER_PARTS',
      correlation_id: partsCorrelationId,
      payload: partResponse,
    });
    success('Part information updated.');
  };

  const onSave = async () => {
    if (!fromWorkOrderCreate) {
      if (!addPart) {
        // eslint-disable-next-line no-unused-expressions
        window?._amplitudeClient?.logEvent('Added Parts', {
          'Work Order ID': workOrderId,
          'Project Name': workOrderProjectName ?? '',
          'Usage Code': partUsageData?.usageCode,
          'Work Order Status': workOrderStatus ?? '',
          'Part ID': newPart?.externalId,
          'OEM Number ': newPart?.externalNumber,
          'Part Description': newPart?.description,
          'Retained by Customer': partUsageData?.customerRetained,
          Returnable: newPart?.returnable,
          'Return Tracking Number': partUsageData?.returnTracking,
          Carrier: newPart?.carrier,
          Note: partUsageData?.note,
          'Parts Added By': selectedPartsAddedBy ?? '',
        });
      }
      try {
        await handleCreateAndUpdate();
        // eslint-disable-next-line no-empty
      } catch (_) {
        error(
          `Failed to ${
            addPart ? 'save' : 'update'
          } part information, please try again later.`,
        );
      }
      onClose();
    }
  };

  const renderProvider = () => (
    <Usage
      partData={newPart}
      usageData={partUsageData}
      setUsageData={setPartUsageData}
      usageCodes={usageCodes}
      onStateChange={onStateChange}
      fromWorkOrderCreate={fromWorkOrderCreate}
      returnTrackingRequired={returnTrackingRequired}
      setReturnTrackingRequired={setReturnTrackingRequired}
      isProvider={isProvider}
    />
  );

  const renderPartsAddedByToggle = () => (
    <div className={styles['parts-added-by']}>
      <p className={styles['parts-added-by-label']}>Who will provide part information?</p>
      <ActionTabs
        items={PARTS_ADDED_BY_OPTIONS.map((option) => ({
          label: option.label,
          onClick: () => !hasExistingParts && handlePartsAddedByChange(option.value),
          active: selectedPartsAddedBy === option.value,
        }))}
      />
    </div>
  );

  const renderProviderBanner = () => (
    <div className={styles['provider-banner']}>
      <span>&#9432;</span>
      <span>
        The assigned provider will add parts and return information with their work order tasks.
      </span>
    </div>
  );

  const partsForm = isProvider ? (
    <>{renderProvider()}</>
  ) : fromWorkOrderCreate ? (
    <div className={styles['part-container']}>
      <div className={styles['part-header-row']}>
        <p className={styles.header}>New part</p>
        <div className={styles['delete-button']}>
          <Button
            icon="deleteHollow"
            onClick={() => {
              if (onDelete && newPart?.id) {
                onDelete(newPart.id);
              }
            }}
            shouldUseIconComponent
            type="text"
          />
        </div>
      </div>
      <AddPart
        newPart={newPart}
        setNewPart={setNewPart}
        usageData={partUsageData}
        setUsageData={setPartUsageData}
        onStateChange={onStateChange}
        fromWorkOrderCreate={fromWorkOrderCreate}
        addPart={addPart}
        isUniqueExternalId={isUniqueExternalId}
        setIsUniqueExternalId={setIsUniqueExternalId}
        hideHeader
      />
    </div>
  ) : (
    <>
      {renderPartsAddedByToggle()}

      {selectedPartsAddedBy === 'assigned_provider' && renderProviderBanner()}

      {selectedPartsAddedBy === 'buyer' && (
        <>
          {!addPart && (
            <ActionTabs
              items={[
                {
                  onClick: () => setTab(TABS.DETAILS),
                  label: 'Details',
                  active: tab === TABS.DETAILS,
                },
                {
                  onClick: () => setTab(TABS.USAGE),
                  label: 'Usage',
                  active: tab === TABS.USAGE,
                },
              ]}
            />
          )}

          {tab === TABS.DETAILS && (
            <AddPart
              newPart={newPart}
              setNewPart={setNewPart}
              usageData={partUsageData}
              setUsageData={setPartUsageData}
              onStateChange={onStateChange}
              fromWorkOrderCreate={fromWorkOrderCreate}
              addPart={addPart}
              isUniqueExternalId={isUniqueExternalId}
              setIsUniqueExternalId={setIsUniqueExternalId}
            />
          )}

          {tab === TABS.USAGE && (
            <Usage
              partData={newPart}
              usageData={partUsageData}
              setUsageData={setPartUsageData}
              usageCodes={usageCodes}
              onStateChange={onStateChange}
              fromWorkOrderCreate={fromWorkOrderCreate}
              returnTrackingRequired={returnTrackingRequired}
              setReturnTrackingRequired={setReturnTrackingRequired}
              isProvider={isProvider}
            />
          )}
        </>
      )}
    </>
  );

  const getModalHeader = useCallback(
    () => (isProvider ? 'Enter part usage' : `${addPart ? 'Add' : 'Edit'} part`),
    [addPart, isProvider],
  );

  return (
    <>
      {!fromWorkOrderCreate ? (
        <Modal
          header={getModalHeader()}
          isOpen={openEditModal}
          onClose={onClose}
          size="medium"
          position="default"
          footerActions={[
            {
              label: 'Cancel',
              type: 'secondary',
              onClick: onClose,
            },
            {
              label: 'Save',
              type: 'primary',
              onClick: onSave,
              disabled: !canSubmit,
            },
          ]}
        >
          {partsForm}
        </Modal>
      ) : (
        <>{partsForm}</>
      )}
    </>
  );
};

PartsForm.defaultProps = {
  usageCodes: USAGE_CODES,
  onStateChange: null,
  onDelete: null,
  partsAddedBy: null,
  onPartsAddedByChange: null,
  hasExistingParts: false,
};

export default PartsForm;
